import { useState } from 'react';
import { V2Badge } from '@sakhtar/design-system/react';
import { dueTone, relativeWhen } from '../../../lib/date/relativeWhen.js';
import { displayJalaliDate } from '../../../lib/date/displayJalali.js';
import { faDigits } from '../../../lib/faDigits.js';
import { formatRialFa, type AmountDisplayUnit } from '../../../lib/finance-display.js';
import type { LeadBoardResponse, LeadRow } from '../../../lib/types/crm.js';
import { LEAD_PRIORITY_TONE } from './leadUi.js';

/**
 * Pipeline board — open leads grouped by the pipeline's real stages. A card
 * moves forward by drag and drop or by its «انتقال به» selector (keyboard and
 * touch); the API only accepts moves to a later stage, so earlier columns
 * are not valid drop targets. Won and lost leads leave the board — they live
 * in the list under «برنده‌شده» / «از دست رفته».
 *
 * UI_REFERENCE_SOURCE: Sakhtar Sales CRM.dc.html §03 PIPELINE — column
 *   header «count | Rial total», card with customer, amount and next step.
 */
export function LeadBoard({
  board,
  unit,
  unitLabel,
  busyRef,
  onOpen,
  onMove,
}: {
  board: LeadBoardResponse;
  unit: AmountDisplayUnit;
  unitLabel: string;
  busyRef: string | null;
  onOpen: (ref: string) => void;
  onMove: (lead: LeadRow, stageRef: string) => void;
}) {
  const [dragging, setDragging] = useState<{ ref: string; sortOrder: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const orderOf = (stageRef: string | undefined) =>
    board.stages.find((stage) => stage.ref === stageRef)?.sortOrder ?? 0;

  return (
    <div className="sk-lead-board" role="list" aria-label="قیف فروش">
      <style>{BOARD_CSS}</style>
      {board.stages.map((stage) => {
        const droppable = dragging !== null && stage.sortOrder > dragging.sortOrder;
        return (
          <section
            key={stage.ref}
            role="listitem"
            aria-label={stage.name}
            className={`sk-lead-col${droppable ? ' is-droppable' : ''}${over === stage.ref && droppable ? ' is-over' : ''}`}
            onDragOver={(event) => {
              if (!droppable) return;
              event.preventDefault();
              setOver(stage.ref);
            }}
            onDragLeave={() => setOver((current) => (current === stage.ref ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              setOver(null);
              const ref = event.dataTransfer.getData('text/plain');
              const lead = board.stages.flatMap((s) => s.leads).find((row) => row.ref === ref);
              setDragging(null);
              if (lead && stage.sortOrder > orderOf(lead.deal.stage?.ref)) onMove(lead, stage.ref);
            }}
          >
            <header className="sk-lead-col__head">
              <div className="sk-lead-col__title">
                <strong>{stage.name}</strong>
                <span className="sk-lead-col__count">{faDigits(stage.count)}</span>
              </div>
              <div className="sk-lead-col__sum">
                {formatRialFa(stage.amountRial, unit)} {unitLabel}
                <span> · احتمال {faDigits(stage.probabilityPct)}٪</span>
              </div>
            </header>
            <div className="sk-lead-col__body">
              {stage.leads.length === 0 ? (
                <div className="sk-lead-col__empty">سرنخی در این مرحله نیست</div>
              ) : (
                stage.leads.map((lead) => {
                  const tone = dueTone(
                    lead.nextAction?.dueAt ?? lead.deal.ownNextAction?.dueAt ?? null,
                  );
                  const later = board.stages.filter((s) => s.sortOrder > stage.sortOrder);
                  return (
                    <article
                      key={lead.ref}
                      className="sk-lead-card"
                      draggable={busyRef !== lead.ref}
                      aria-busy={busyRef === lead.ref}
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/plain', lead.ref);
                        event.dataTransfer.effectAllowed = 'move';
                        setDragging({ ref: lead.ref, sortOrder: stage.sortOrder });
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                      }}
                    >
                      <button
                        type="button"
                        className="sk-lead-card__main"
                        onClick={() => onOpen(lead.ref)}
                      >
                        <span className="sk-lead-card__name">{lead.displayName}</span>
                        <span className="sk-lead-card__sub">
                          {lead.deal.customer
                            ? `مشتری: ${lead.deal.customer.label}`
                            : (lead.needSubject ?? lead.ref)}
                        </span>
                        <span className="sk-lead-card__amount">
                          {formatRialFa(lead.deal.amountRial, unit)} {unitLabel}
                        </span>
                      </button>
                      <div className="sk-lead-card__meta">
                        <V2Badge tone={LEAD_PRIORITY_TONE[lead.deal.priority]}>
                          {lead.deal.priorityLabel}
                        </V2Badge>
                        {lead.deal.expectedCloseDate ? (
                          <span title="تاریخ احتمالی بستن">
                            {displayJalaliDate(lead.deal.expectedCloseDate)}
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="sk-lead-card__next"
                        style={{ color: tone === 'overdue' ? 'var(--sk-danger)' : undefined }}
                      >
                        {lead.nextAction
                          ? `${lead.nextAction.typeLabel}${lead.nextAction.dueAt ? ` — ${relativeWhen(lead.nextAction.dueAt)}` : ''}`
                          : lead.deal.ownNextAction
                            ? `${lead.deal.ownNextAction.title}${lead.deal.ownNextAction.dueAt ? ` — ${relativeWhen(lead.deal.ownNextAction.dueAt)}` : ''}`
                            : 'بدون اقدام بعدی'}
                      </div>
                      {later.length > 0 ? (
                        <select
                          className="sk-select sk-lead-card__move"
                          aria-label={`انتقال ${lead.displayName} به مرحلهٔ بعد`}
                          value=""
                          disabled={busyRef === lead.ref}
                          onChange={(event) => {
                            if (event.target.value) onMove(lead, event.target.value);
                          }}
                        >
                          <option value="">انتقال به…</option>
                          {later.map((next) => (
                            <option key={next.ref} value={next.ref}>
                              {next.name}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </article>
                  );
                })
              )}
              {stage.hasMore ? (
                <div className="sk-lead-col__more">
                  {faDigits(stage.count - stage.leads.length)} سرنخ دیگر — برای دیدن همه از نمای
                  فهرست استفاده کنید.
                </div>
              ) : null}
            </div>
          </section>
        );
      })}
    </div>
  );
}

const BOARD_CSS = `
.sk-lead-board { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(250px, 1fr); gap: 12px; overflow-x: auto; padding: 14px 16px 18px; align-items: start; }
.sk-lead-col { background: var(--sk-bg-canvas); border: 1px solid var(--sk-border); border-radius: var(--sk-r-lg); display: flex; flex-direction: column; min-height: 140px; transition: border-color .15s, background .15s; }
.sk-lead-col.is-droppable { border-style: dashed; border-color: var(--sk-brand-300); }
.sk-lead-col.is-over { background: color-mix(in oklch, var(--sk-brand-500), transparent 92%); border-color: var(--sk-brand-500); }
.sk-lead-col__head { padding: 12px 12px 10px; border-bottom: 1px solid var(--sk-divider); display: flex; flex-direction: column; gap: 4px; }
.sk-lead-col__title { display: flex; align-items: center; justify-content: space-between; font-size: 14px; }
.sk-lead-col__count { font-size: 12px; background: var(--sk-bg-elev); border: 1px solid var(--sk-border); border-radius: 999px; padding: 0 8px; color: var(--sk-fg-muted); }
.sk-lead-col__sum { font-size: 12px; color: var(--sk-fg-muted); }
.sk-lead-col__sum span { color: var(--sk-fg-subtle); }
.sk-lead-col__body { padding: 10px; display: flex; flex-direction: column; gap: 10px; }
.sk-lead-col__empty, .sk-lead-col__more { font-size: 12px; color: var(--sk-fg-subtle); text-align: center; padding: 14px 6px; line-height: 1.8; }
.sk-lead-card { background: var(--sk-bg-elev); border: 1px solid var(--sk-border); border-radius: var(--sk-r-md); padding: 10px; display: flex; flex-direction: column; gap: 8px; cursor: grab; }
.sk-lead-card[aria-busy="true"] { opacity: .55; cursor: progress; }
.sk-lead-card:hover { border-color: var(--sk-brand-300); }
.sk-lead-card__main { all: unset; cursor: pointer; display: flex; flex-direction: column; gap: 3px; }
.sk-lead-card__main:focus-visible { outline: 2px solid var(--sk-brand-500); outline-offset: 2px; border-radius: 4px; }
.sk-lead-card__name { font-size: 14px; font-weight: 600; color: var(--sk-fg); }
.sk-lead-card__sub { font-size: 12px; color: var(--sk-fg-muted); }
.sk-lead-card__amount { font-size: 13px; font-weight: 600; color: var(--sk-brand-600); font-variant-numeric: tabular-nums; }
.sk-lead-card__meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 12px; color: var(--sk-fg-muted); }
.sk-lead-card__next { font-size: 12px; color: var(--sk-fg-muted); }
.sk-lead-card__move { font-size: 12px; height: 30px; }
`;
